import {SetState} from "../../util/FormHandlers";
import {Employee, UNASSIGNED_NEIGHBOURHOOD_ID} from "../../api/Model";
import {useEmployeeNeighbourhoodStore} from "../../api/EmployeeNeighbourhoodStore";
import {useEmployeeHierarchyStore} from "../../api/EmployeeHierarchyStore";
import {useNewJoinerStore} from "../../api/NewJoinerStore";
import {RemoteValue} from "../../util/RemoteValue";
import {Loading} from "../../components/loading-data/LoadingData";
import EmployeeAvatar from "../../components/employee-avatar/EmployeeAvatar";
import {useCallback, useMemo} from "react";
import {Button, Stack} from "@mui/material";
import {HierarchyEmployee} from "../../util/EmployeeHierarchy";
import {emptyHierarchyEmployee} from "../../api/Empty";


function SelectEmployeeButton({employee, setSelectedEmployee, setJoinDate}: {
    employee: HierarchyEmployee,
    setSelectedEmployee: SetState<Employee | undefined>,
    setJoinDate: SetState<string>,
}) {
    const handleClick = useCallback(() => {
        setSelectedEmployee(employee);
        if (employee.startDate) {
            setJoinDate(employee.startDate);
        }
    }, [employee, setSelectedEmployee, setJoinDate]);

    return (
        <Button
            startIcon={<EmployeeAvatar employee={employee} sx={{width: 24, height: 24}}/>}
            onClick={handleClick}
            variant="outlined"
        >
            {employee.fullName}
        </Button>
    );
}

export function NeighbourhoodlessEmployees({setSelectedEmployee, setJoinDate}: {
    setSelectedEmployee: SetState<Employee | undefined>,
    setJoinDate: SetState<string>,
}) {
    const employeeNeighbourhoods = useEmployeeNeighbourhoodStore(state => state.remoteValue);
    const employeeHierarchy = useEmployeeHierarchyStore(employeeNeighbourhoods);
    const newJoiners = useNewJoinerStore(state => state.remoteValue);
    const neighbourhoodlessEmployees = useMemo(() =>
            RemoteValue.combine({employeeHierarchy, newJoiners})
                .map(({employeeHierarchy, newJoiners}) => {
                    const newJoinerEmployeeIds = new Set(
                        newJoiners.flatMap(newJoiner =>
                            newJoiner.person.type === 'existing' ? [newJoiner.person.employee.id] : []
                        )
                    );

                    return employeeHierarchy.employees.filter(employee =>
                        !newJoinerEmployeeIds.has(employee.id)
                        && employee.neighbourhoods.some(hood => hood.id === UNASSIGNED_NEIGHBOURHOOD_ID)
                    );
                }),
        [employeeHierarchy, newJoiners],
    );

    return (
        <Loading
            value={neighbourhoodlessEmployees}
            errorElement="Failed to load neighbourhoodless employees"
            skeletonValue={[emptyHierarchyEmployee]}
        >{neighbourhoodlessEmployees =>
            neighbourhoodlessEmployees.length > 0 ? (
                <Stack gap={2} direction="row" flexWrap="wrap">
                    {neighbourhoodlessEmployees.map(employee => (
                        <SelectEmployeeButton
                            key={employee.id}
                            employee={employee}
                            setSelectedEmployee={setSelectedEmployee}
                            setJoinDate={setJoinDate}
                        />
                    ))}
                </Stack>
            ) : null
        }</Loading>
    );
}